import React, { useMemo, useState } from 'react';

import { Table, Input, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { SearchOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { CustomAttributesTags } from '@components/shared/inventory_attribute_definitions/CustomAttributesTags';
import { useInventoryAttributeDefinitions } from '@hooks/orders/useInventoryAttributeDefinitions';
import { computeShelfStats, isStorageShelf, shelfEntryKey } from './inventoryFragments';

const { Text } = Typography;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
  flex-wrap: wrap;
`;

const CountText = styled(Text)`
  margin-left: auto;
`;

interface ShelfInventoryEntry {
  id: string;
  shelf_id: string;
  shelf_location: string | null;
  shelf_type_name: string | null;
  shelf_role: string | null;
  quantity: number;
}

interface InventoryRow {
  id: string;
  inventory_type?: string | null;
  batch?: string | null;
  expiration_date?: string | null;
  manufacturing_date?: string | null;
  custom_attributes?: Record<string, unknown> | null;
  shelf_inventories?: ShelfInventoryEntry[];
  [key: string]: unknown;
}

interface FlatShelfRow {
  key: string;
  shelf_id: string;
  shelf_location: string | null;
  quantity: number;
  inventory_type?: string | null;
  batch?: string | null;
  expiration_date?: string | null;
  manufacturing_date?: string | null;
  custom_attributes?: Record<string, unknown> | null;
  is_fragment: boolean;
}

interface ProductShelfDistributionProps {
  inventories?: InventoryRow[];
  loading?: boolean;
  merchantId?: string;
  role?: 'operator' | 'admin';
}

/**
 * 儲位分佈視角：把每筆 inventory 內嵌的 shelf_inventories 攤平成「一列 = 一個儲位
 * × 一筆批次」，直接回答「這商品放在哪幾個儲位、各幾個」。只列正式 storage 儲位
 * （暫存區待上架 / 移動載具是臨時位置，於庫存批次視角與總覽呈現）；資料來自
 * get_records，不需額外 API。
 *
 * 欄位帶齊一筆庫存的身分屬性：類型 / 批號 / 效期 / 製造日期 / 自定義屬性。
 */
const ProductShelfDistribution: React.FC<ProductShelfDistributionProps> = ({
  inventories = [],
  loading = false,
  merchantId,
  role = 'operator',
}) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');

  // 自定義屬性的 name → label 對照（缺定義時 tag 內 fallback 成原始 key）。
  const attributeDefinitions = useInventoryAttributeDefinitions(role, merchantId);
  const attributeLabelByName = useMemo(
    () => Object.fromEntries(attributeDefinitions.map((def) => [def.name, def.label ?? def.name])),
    [attributeDefinitions],
  );

  const { fragmentKeys } = useMemo(() => computeShelfStats(inventories), [inventories]);

  const allRows = useMemo<FlatShelfRow[]>(() => {
    const rows: FlatShelfRow[] = [];
    inventories.forEach((inv) => {
      (inv.shelf_inventories || []).forEach((si) => {
        if (!isStorageShelf(si)) return;
        rows.push({
          key: shelfEntryKey(inv.id, si.id),
          shelf_id: si.shelf_id,
          shelf_location: si.shelf_location,
          quantity: si.quantity,
          inventory_type: inv.inventory_type,
          batch: inv.batch,
          expiration_date: inv.expiration_date,
          manufacturing_date: inv.manufacturing_date,
          custom_attributes: inv.custom_attributes,
          is_fragment: fragmentKeys.has(shelfEntryKey(inv.id, si.id)),
        });
      });
    });
    return rows;
  }, [inventories, fragmentKeys]);

  const filteredRows = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    if (!keyword) return allRows;
    return allRows.filter((row) => {
      const location = (row.shelf_location || '').toLowerCase();
      const batch = (row.batch || '').toLowerCase();
      return location.includes(keyword) || batch.includes(keyword);
    });
  }, [allRows, search]);

  const shelfCount = useMemo(
    () => new Set(filteredRows.map((row) => row.shelf_id)).size,
    [filteredRows],
  );

  const columns: ColumnsType<FlatShelfRow> = [
    {
      title: t('models.shelf.location'),
      dataIndex: 'shelf_location',
      key: 'shelf_location',
      defaultSortOrder: 'ascend',
      sorter: (a, b) =>
        (a.shelf_location || '').localeCompare(b.shelf_location || '', undefined, { numeric: true }),
      render: (text: string | null, record) => (
        <Link to={`/shelves/${record.shelf_id}`} style={{ fontWeight: 'bold' }}>
          {text || '-'}
        </Link>
      ),
    },
    {
      title: t('models.inventory.inventory_type'),
      dataIndex: 'inventory_type',
      key: 'inventory_type',
      width: 120,
      render: (text: string | null | undefined) => text || '-',
    },
    {
      title: t('models.inventory.batch'),
      dataIndex: 'batch',
      key: 'batch',
      width: 120,
      render: (text: string | null | undefined) => text || '-',
    },
    {
      title: t('models.inventory.expiration_date'),
      dataIndex: 'expiration_date',
      key: 'expiration_date',
      width: 120,
      render: (text: string | null | undefined) => text || '-',
    },
    {
      title: t('models.inventory.manufacturing_date'),
      dataIndex: 'manufacturing_date',
      key: 'manufacturing_date',
      width: 120,
      render: (text: string | null | undefined) => text || '-',
    },
    {
      title: t('models.inventory.custom_attributes'),
      dataIndex: 'custom_attributes',
      key: 'custom_attributes',
      width: 200,
      render: (customAttributes: Record<string, unknown> | null | undefined) => (
        <CustomAttributesTags attributes={customAttributes} attributeLabelByName={attributeLabelByName} />
      ),
    },
    {
      title: t('models.inventory.quantity'),
      dataIndex: 'quantity',
      key: 'quantity',
      width: 140,
      align: 'right',
      sorter: (a, b) => a.quantity - b.quantity,
      render: (qty: number, record) =>
        record.is_fragment ? (
          <span>
            <Text type="danger" strong>
              {qty}
            </Text>{' '}
            <Tag color="error">{t('shared.inventory.fragment_tag')}</Tag>
          </span>
        ) : (
          qty
        ),
    },
  ];

  return (
    <div>
      <Toolbar>
        <Input
          prefix={<SearchOutlined />}
          placeholder={t('shared.inventory.shelf_search_placeholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          allowClear
          style={{ width: 240 }}
        />
        <CountText type="secondary">
          {t('shared.inventory.shelf_count', { count: shelfCount })}
        </CountText>
      </Toolbar>
      <Table
        columns={columns}
        dataSource={filteredRows}
        rowKey="key"
        loading={loading}
        pagination={false}
        scroll={{ x: 'max-content' }}
        locale={{ emptyText: t('shared.inventory.shelf_empty') }}
      />
    </div>
  );
};

export default ProductShelfDistribution;
